import { useEffect, useState } from "react";
import api, { formatINR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BookOpen, Plus, Edit, Infinity as InfinityIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const empty = { name: "", code: "", description: "", total_fee: 0, duration_months: 12, is_lifetime: false, required_documents: [] };

export default function Programs() {
  const [programs, setPrograms] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [docsText, setDocsText] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";

  const load = async () => {
    const { data } = await api.get("/programs");
    setPrograms(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(empty);
    setDocsText("Aadhaar Card\n10th Marksheet\n12th Marksheet\nPassport Photo");
    setEditId(null);
    setOpen(true);
  };
  const openEdit = (p) => {
    setForm({ ...p });
    setDocsText((p.required_documents || []).join("\n"));
    setEditId(p.id);
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = {
        ...form,
        total_fee: parseFloat(form.total_fee) || 0,
        duration_months: form.is_lifetime ? 0 : (parseInt(form.duration_months) || 12),
        required_documents: docsText.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editId) await api.put(`/programs/${editId}`, payload);
      else await api.post("/programs", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="programs-page">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <div className="label-caps mb-2 flex items-center gap-2"><BookOpen size={12} /> Configuration</div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Programs</h1>
          <p className="text-slate-500 mt-1">Define training programs, fees and required documents.</p>
        </div>
        {isAdmin && (
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={openNew} data-testid="add-program-button">
            <Plus size={16} className="mr-2" /> New Program
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {programs.map((p) => (
          <Card key={p.id} className="p-6" data-testid={`program-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className="font-mono">{p.code}</Badge>
                  {p.is_lifetime && (
                    <Badge variant="outline" className="status-paid">
                      <InfinityIcon size={12} className="mr-1" /> Lifetime
                    </Badge>
                  )}
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.description}</p>
              </div>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-program-${p.id}`}>
                  <Edit size={14} />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t">
              <div>
                <div className="label-caps">Total Fee</div>
                <div className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>{formatINR(p.total_fee)}</div>
              </div>
              <div>
                <div className="label-caps">Duration</div>
                <div className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>
                  {p.is_lifetime ? "Lifetime" : `${p.duration_months} mo`}
                </div>
              </div>
            </div>
            <div className="mt-4">
              <div className="label-caps mb-2">Required Documents</div>
              <div className="flex flex-wrap gap-1">
                {(p.required_documents || []).map((d) => <Badge key={d} variant="outline" className="text-xs">{d}</Badge>)}
              </div>
            </div>
          </Card>
        ))}
        {programs.length === 0 && (
          <div className="md:col-span-2 text-center py-16 text-slate-500">
            No programs yet. {isAdmin && "Click 'New Program' to create one."}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Program" : "New Program"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="label-caps">Name</Label><Input data-testid="prog-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" /></div>
            <div><Label className="label-caps">Code</Label><Input data-testid="prog-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-2 font-mono" /></div>
            <div><Label className="label-caps">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-caps">Total Fee (₹)</Label>
                <Input data-testid="prog-fee" type="number" value={form.total_fee} onChange={(e) => setForm({ ...form, total_fee: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label className="label-caps">Duration (months)</Label>
                <Input type="number" disabled={form.is_lifetime} value={form.is_lifetime ? "" : form.duration_months} onChange={(e) => setForm({ ...form, duration_months: e.target.value })} className="mt-2" placeholder={form.is_lifetime ? "Lifetime" : ""} />
              </div>
            </div>
            <label className="flex items-center gap-2 p-3 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer">
              <Checkbox checked={form.is_lifetime} onCheckedChange={(v) => setForm({ ...form, is_lifetime: !!v })} data-testid="prog-lifetime" />
              <div>
                <div className="text-sm font-medium flex items-center gap-1">
                  <InfinityIcon size={14} /> Lifetime Course
                </div>
                <div className="text-xs text-slate-500">No fixed duration. Students stay enrolled until selected (भरती पर्यंत).</div>
              </div>
            </label>
            <div><Label className="label-caps">Required Documents (one per line)</Label><Textarea data-testid="prog-docs" value={docsText} onChange={(e) => setDocsText(e.target.value)} className="mt-2 font-mono text-sm" rows={5} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={submit} data-testid="save-program">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
